import React from 'react';
import {Marked} from 'marked';
import {markedHighlight} from 'marked-highlight';
import hljs from 'highlight.js';
import markedKatex from 'marked-katex-extension';
import 'highlight.js/styles/atom-one-dark.css';
import 'katex/dist/katex.css';
import {Parser, ProcessNodeDefinitions} from 'html-to-react';
import {Button, useTheme} from '@dssf/component-library';

type MarkdownViewerProps = {
  /**
   * The markdown content to display.
   */
  value?: string;
};

const supportedHljsLanguages = hljs.listLanguages();

export function MarkdownViewer({value, ...props}: MarkdownViewerProps) {
  const {tokens} = useTheme();

  let insideCodeBlock = false;
  const preparedContent = new Marked(
    markedHighlight({
      highlight(code, language) {
        let highlighted;
        if (language && supportedHljsLanguages.includes(language)) {
          try {
            highlighted = hljs.highlight(code, {language, ignoreIllegals: true});
          } catch (e) {
          } // highlight.js can throw an error if the language is not supported
        }
        if (!highlighted) {
          highlighted = hljs.highlightAuto(code);
        }

        // create element to ensure code attribute is correctly encoded
        const copyButton = document.createElement('div');
        copyButton.dataset.copybutton = 'true';
        copyButton.style.position = 'sticky';
        copyButton.style.right = '0.5rem';
        copyButton.style.flexShrink = '0';
        copyButton.dataset.code = code;

        return `<pre class="hljs" style="display: flex; align-items: end; gap: 1rem;"><code class="language-${highlighted?.language ?? 'plaintext'}">${highlighted.value ?? code}</code>${copyButton.outerHTML}</pre>`; // markedHighlight by default does not add the hljs class, so we are adding it here where we have access to the language and just pass the raw html in the renderer
      },
    }),
    {},
    markedKatex({throwOnError: false}),
  ).parse(
    (value ?? '')
      .split('\n')
      .map(line => {
        // add indentations for the marked library to display code snippets and ordered lists
        let considerInsideCodeBlock = insideCodeBlock;
        if (line.includes('```')) {
          considerInsideCodeBlock = true;
          insideCodeBlock = !insideCodeBlock;
        }
        return considerInsideCodeBlock && !line.startsWith('   ') ? `   ${line}` : line;
      })
      .join('\n')
      .replace(/(\\\[|\\]|\\\(|\\\))|^\$/gm, ' $ '), // for formula rendering we need to replace GPT4 escape characters \[...\] and \(...\) with $...$ for katex
  );

  const parsedContent = Parser().parseWithInstructions(preparedContent, () => true, [
    {
      // code copy buttons
      replaceChildren: true,
      shouldProcessNode: (node: any) => node.attribs?.['data-copybutton'] === 'true',
      processNode: (node: any) => CodeCopyButton({code: node.attribs['data-code']}),
    },
    {
      // Anything else
      shouldProcessNode: () => true,
      processNode: ProcessNodeDefinitions().processDefaultNode,
    },
  ]);

  return (
    <div
      css={{
        fontFamily: 'Bosch Sans,sans-serif',
        pre: {padding: '0.5rem', overflowX: 'auto'},
        'ul, ol': {paddingLeft: 'revert'},
        p: {marginTop: '0 !important'},
        '& > *': {marginBottom: '1.5rem'},
        '& > *:last-child': {marginBottom: 0},
        a: {
          color: tokens.colors['bosch-blue-50'],
          textDecoration: 'none',
          '&:hover': {textDecoration: 'underline'},
        },
      }}
      {...props}
    >
      {parsedContent}
    </div>
  );
}

function CodeCopyButton({code}: { code: string }) {
  return (
    <Button variant="icononly" iconColor="white" icon="copy" onClick={() => navigator.clipboard.writeText(code)}/>
  );
}
